import dayjs from '../../utils/dayjsConfig'
import { DateObject } from 'react-multi-date-picker'
import { REFUND_FILTER_KEYS } from '../constants/refundStatus'

export { REFUND_FILTER_KEYS }

export const createEmptyDateRange = () => ({
  start: null,
  end: null
})

export const buildSelectValue = (values = []) => ({
  mode: Array.isArray(values) && values.length > 0 ? 'custom' : 'all',
  values: Array.isArray(values) ? [...values] : []
})

export const createEmptyDraftFilterSelections = () => ({
  [REFUND_FILTER_KEYS.CLIENT]: buildSelectValue(),
  [REFUND_FILTER_KEYS.USER]: buildSelectValue(),
  [REFUND_FILTER_KEYS.STATUS]: buildSelectValue(),
  [REFUND_FILTER_KEYS.WAREHOUSE]: buildSelectValue()
})

export const getSelectValues = (selectValue) => {
  if (selectValue?.mode !== 'custom') return []
  return Array.isArray(selectValue.values) ? selectValue.values : []
}

const toDayjsValue = (value) => {
  if (!value) return null
  if (dayjs.isDayjs(value)) return value.startOf('day')

  if (typeof value?.toDate === 'function') {
    const fromDateObject = dayjs(value.toDate())
    if (fromDateObject.isValid()) return fromDateObject.startOf('day')
  }

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.startOf('day') : null
}

export const normalizeDateRange = (range) => {
  const start = toDayjsValue(range?.start)
  const end = toDayjsValue(range?.end)

  if (!start && !end) return createEmptyDateRange()
  if (start && !end) return { start, end: null }
  if (!start && end) return { start: null, end }
  if (start.isAfter(end, 'day')) return { start: end, end: start }

  return { start, end }
}

export const buildAppliedDateRange = (range) => {
  const normalizedRange = normalizeDateRange(range)

  if (normalizedRange.start && !normalizedRange.end) {
    return {
      start: normalizedRange.start,
      end: normalizedRange.start
    }
  }

  if (!normalizedRange.start && normalizedRange.end) {
    return {
      start: normalizedRange.end,
      end: normalizedRange.end
    }
  }

  return normalizedRange
}

export const pickerValueToDateRange = (pickerValue) => {
  if (!Array.isArray(pickerValue) || pickerValue.length === 0) {
    return createEmptyDateRange()
  }

  const orderedValues = pickerValue
    .map((value) => toDayjsValue(value))
    .filter(Boolean)
    .sort((left, right) => left.valueOf() - right.valueOf())

  if (orderedValues.length === 0) return createEmptyDateRange()
  if (orderedValues.length === 1) {
    return { start: orderedValues[0], end: null }
  }

  return {
    start: orderedValues[0],
    end: orderedValues[orderedValues.length - 1]
  }
}

export const dateRangeToPickerValue = (range) => {
  const appliedRange = buildAppliedDateRange(range)

  if (!appliedRange.start) return null

  if (appliedRange.start.isSame(appliedRange.end, 'day')) {
    return [new DateObject(appliedRange.start.toDate())]
  }

  return [new DateObject(appliedRange.start.toDate()), new DateObject(appliedRange.end.toDate())]
}

export const dateRangeToQuery = (range) => {
  const appliedRange = buildAppliedDateRange(range)

  if (!appliedRange.start) return null

  return `&start_date=${appliedRange.start.toISOString()}&end_date=${appliedRange.end.endOf('day').toISOString()}`
}

export const formatDateRangeSummary = (range, emptyText = 'Sin rango seleccionado') => {
  const appliedRange = buildAppliedDateRange(range)

  if (!appliedRange.start) return emptyText

  const startLabel = appliedRange.start.locale('es').format('DD MMMM')
  const endLabel = appliedRange.end.locale('es').format('DD MMMM')

  if (appliedRange.start.isSame(appliedRange.end, 'day')) {
    return startLabel
  }

  return `${startLabel} - ${endLabel}`
}

export const buildAppliedChipLabel = ({
  singularLabel,
  pluralLabel,
  values,
  idToLabelMapping
}) => {
  if (!Array.isArray(values) || values.length === 0) return ''

  if (values.length === 1) {
    const resolvedLabel = idToLabelMapping.get(values[0]) || String(values[0])
    return `${singularLabel}: ${resolvedLabel}`
  }

  return `${pluralLabel}: ${values.length}`
}

export const countSelectedGroups = (filterSelections, dateRange) => {
  let selectedGroups = 0

  Object.values(REFUND_FILTER_KEYS).forEach((filterKey) => {
    if (getSelectValues(filterSelections?.[filterKey]).length > 0) {
      selectedGroups += 1
    }
  })

  if (buildAppliedDateRange(dateRange).start) {
    selectedGroups += 1
  }

  return selectedGroups
}
